import React, { useState } from 'react';
import { Button } from './UIComponents';
import { FileText, FileJson, FileType, Check } from 'lucide-react';

interface ExportTranscriptProps {
  onExport: (format: string, options: ExportOptions) => void;
  onCancel: () => void;
  isExporting?: boolean;
}

export interface ExportOptions {
  includeSpeakerNames: boolean;
  includeTimecodes: boolean;
}

const EXPORT_FORMATS = [
  { id: 'txt', label: 'Text (.txt)', icon: FileText, description: 'Plain text file' },
  { id: 'srt', label: 'Subtitles (.srt)', icon: FileType, description: 'Standard subtitle format' },
  { id: 'vtt', label: 'WebVTT (.vtt)', icon: FileType, description: 'Web video text tracks' },
  { id: 'docx', label: 'Word (.docx)', icon: FileText, description: 'Microsoft Word document' },
  { id: 'pdf', label: 'PDF (.pdf)', icon: FileText, description: 'Portable Document Format' },
  { id: 'json', label: 'JSON', icon: FileJson, description: 'Structured data' },
  { id: 'md', label: 'Markdown', icon: FileText, description: 'Formatted text' },
];

const ExportTranscript: React.FC<ExportTranscriptProps> = ({ onExport, onCancel, isExporting = false }) => {
  const [selectedFormat, setSelectedFormat] = useState('txt');
  const [options, setOptions] = useState<ExportOptions>({
    includeSpeakerNames: true,
    includeTimecodes: true,
  });

  const handleOptionChange = (key: keyof ExportOptions) => {
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-3">
          Select Format
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {EXPORT_FORMATS.map((format) => {
            const Icon = format.icon;
            const isSelected = selectedFormat === format.id;
            return (
              <div
                key={format.id}
                onClick={() => setSelectedFormat(format.id)}
                className={`relative group cursor-pointer rounded-xl border p-4 transition-all duration-200 ${
                  isSelected
                    ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-500'
                    : 'border-slate-200 bg-white hover:border-indigo-300 hover:shadow-sm dark:bg-zinc-800 dark:border-zinc-700 dark:hover:border-indigo-500/50'
                }`}
              >
                <div className="flex flex-col items-center text-center gap-2">
                  <div className={`p-2 rounded-lg transition-colors ${
                    isSelected 
                      ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400' 
                      : 'bg-slate-100 text-slate-500 group-hover:bg-indigo-50 group-hover:text-indigo-500 dark:bg-zinc-700 dark:text-zinc-400 dark:group-hover:bg-indigo-500/10'
                  }`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className={`text-sm font-medium ${
                    isSelected ? 'text-indigo-900 dark:text-indigo-100' : 'text-slate-700 dark:text-zinc-300'
                  }`}>
                    {format.label}
                  </span>
                </div>
                {isSelected && (
                  <div className="absolute top-2 right-2">
                    <div className="bg-indigo-600 text-white rounded-full p-0.5">
                      <Check className="h-3 w-3" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-3 p-4 bg-slate-50 dark:bg-zinc-800/50 rounded-xl border border-slate-100 dark:border-zinc-800">
        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-zinc-400 mb-1">
          Export Options
        </label>
        <div className="space-y-2.5">
          <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-700/50 transition-colors cursor-pointer group">
            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
              options.includeSpeakerNames 
                ? 'bg-indigo-600 border-indigo-600' 
                : 'bg-white border-slate-300 dark:bg-zinc-800 dark:border-zinc-600 group-hover:border-indigo-400'
            }`}>
              {options.includeSpeakerNames && <Check className="h-3.5 w-3.5 text-white" />}
            </div>
            <input 
              type="checkbox" 
              className="hidden" 
              checked={options.includeSpeakerNames}
              onChange={() => handleOptionChange('includeSpeakerNames')}
            />
            <span className="text-sm text-slate-700 dark:text-zinc-300 font-medium">Include speaker names</span>
          </label>

          <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-700/50 transition-colors cursor-pointer group">
            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
              options.includeTimecodes 
                ? 'bg-indigo-600 border-indigo-600' 
                : 'bg-white border-slate-300 dark:bg-zinc-800 dark:border-zinc-600 group-hover:border-indigo-400'
            }`}>
              {options.includeTimecodes && <Check className="h-3.5 w-3.5 text-white" />}
            </div>
            <input 
              type="checkbox" 
              className="hidden"
              checked={options.includeTimecodes}
              onChange={() => handleOptionChange('includeTimecodes')}
            />
            <span className="text-sm text-slate-700 dark:text-zinc-300 font-medium">Include timecodes</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button variant="ghost" onClick={onCancel} disabled={isExporting}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={() => onExport(selectedFormat, options)}
          isLoading={isExporting}
          className="px-6"
        >
          Download {EXPORT_FORMATS.find(f => f.id === selectedFormat)?.label.split(' ')[0]}
        </Button>
      </div>
    </div>
  );
};

export default ExportTranscript;
